/**
 * get_flow_permissions tool - Get who has access to a flow
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const getFlowPermissionsInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID)"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
});

export type GetFlowPermissionsInput = z.infer<typeof getFlowPermissionsInputSchema>;

export const getFlowPermissionsTool = {
  name: "get_flow_permissions",
  description:
    "Get the list of users, groups, and service principals that have access to a flow. Shows their role (Owner, CanEdit, CanView) and identity information. Use this to audit who can see or modify a flow.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID)",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
    },
    required: ["flowId"],
  },
};

export interface FlowPermissionResult {
  principalId: string;
  principalType: string;
  displayName?: string;
  email?: string;
  role: string;
}

export async function handleGetFlowPermissions(
  api: FlowManagementApi,
  input: unknown
): Promise<{ text: string; structured?: Record<string, unknown> }> {
  const parsed = getFlowPermissionsInputSchema.parse(input);

  logger.info({ input: parsed }, "Executing get_flow_permissions");

  const permissions = await api.getFlowPermissions(
    parsed.flowId,
    parsed.environment
  );

  const result: FlowPermissionResult[] = permissions.map((perm) => ({
    principalId: perm.properties.principal.id,
    principalType: perm.properties.permissionType,
    displayName: perm.properties.principal.displayName,
    email: perm.properties.principal.email,
    role: perm.properties.roleName,
  }));

  // Machine-parseable mirror of the prose, emitted as MCP structuredContent (#11).
  const structured: Record<string, unknown> = {
    count: result.length,
    permissions: result.map((perm) => ({
      principalId: perm.principalId,
      principalType: perm.principalType,
      ...(perm.displayName ? { displayName: perm.displayName } : {}),
      ...(perm.email ? { email: perm.email } : {}),
      role: perm.role,
    })),
  };

  // Prose, not a JSON dump: the flow API returns bare principal ids, so at
  // least say which one is the caller and what each role means.
  let me: string | undefined;
  const oidLookup = (api as { getCurrentUserOid?: () => Promise<string | undefined> }).getCurrentUserOid;
  if (typeof oidLookup === "function") {
    try {
      me = await oidLookup.call(api);
    } catch {
      me = undefined;
    }
  }
  const roleText: Record<string, string> = {
    Owner: "Owner (edit, share, delete, run)",
    CanEdit: "Can edit (edit and run, cannot share)",
    CanView: "Run only (can run and see history)",
  };
  const lines = [`Permissions on this flow (${result.length}):`];
  for (const perm of result) {
    const who = perm.displayName ?? perm.email ?? perm.principalId;
    const extra = perm.displayName && perm.email ? ` <${perm.email}>` : "";
    const you = me && perm.principalId.toLowerCase() === me.toLowerCase() ? " (you)" : "";
    const idPart = who === perm.principalId ? ` — ${perm.principalType}` : ` — ${perm.principalType} ${perm.principalId}`;
    lines.push(`  • ${roleText[perm.role] ?? perm.role}: ${who}${extra}${you}${idPart}`);
  }
  if (result.some((perm) => !perm.displayName)) {
    lines.push("Principals are shown by id because the flow API does not return names; resolve them with your directory or share_flow's lookup.");
  }
  return { text: lines.join("\n"), structured };
}
